import { SceneContext } from '@/lib/threeScene/SceneContext';
import { Scene } from 'three';
import { logger, stop, stopAll, type StopHandle } from '@/util';
import { type StandardNode, updateNode } from '@/lib/threeScene/updateNode';
import { watchImmediate } from '@vueuse/core';
import { useXRaysStore, type XRayViewState } from '@/views/dicoms/xrays/xraysViewStore';
import { reactive } from 'vue';
import type { XRay2D3DViewId } from '@/api/xrays';
import {
    type XRaySceneState,
    fullViewCameraParams,
} from '@/views/dicoms/xrays/xraySceneState';
import { XRAY_CAMERA_TARGET_DISTANCE } from '@/views/dicoms/xrays/xraySceneState';
import { vector3 } from '@/domain/geometry/vector3';
import { formatVector } from '@/domain/geometry/formatMath';

const _log = logger('dicom-view');

type _OverviewViewportId = `${XRay2D3DViewId}-overview`;
export type XRayViewportId = 'main' | _OverviewViewportId;

export type XRaySceneNode = StandardNode;

export class XRaySceneContext extends SceneContext<XRaySceneNode, XRaySceneState> {}

export function xraySceneContext(state: XRaySceneState): XRaySceneContext {
    const scene = new Scene();
    const sceneState = reactive(state);
    scene.up.set(0, 0, 1);
    return new XRaySceneContext(scene, sceneState);
}

export function updateXRayScene(context: XRaySceneContext, viewportId: XRayViewportId): StopHandle {
    return stopAll(
        ...Object.values(context.state.nodes).map((node) => updateNode(context, node)),
        viewportId !== 'main'
            ? _updateOverviewScene(context, viewportId)
            : _updateMainScene(context),
    );
}

function _viewIdFromViewportId(viewportId: _OverviewViewportId): XRay2D3DViewId {
    switch (viewportId) {
        case 'ap-overview':
            return 'ap';
        case 'left-oblique-overview':
            return 'left-oblique';
        case 'right-oblique-overview':
            return 'right-oblique';
        default:
            throw new Error(`Unknown viewportId: ${viewportId}`);
    }
}

function _updateOverviewScene(
    context: XRaySceneContext,
    viewportId: _OverviewViewportId,
): StopHandle {
    // Determine the view-id based on the viewportId
    const viewId = _viewIdFromViewportId(viewportId);
    const viewState = useXRaysStore().viewState(viewId);
    const imagePlane = context.state.nodes.imagePlane;
    imagePlane.material = viewState.imageMaterial;

    const camera = context.state.rendering.camera;

    return stopAll(
        watchImmediate(
            () => viewState.imageSize,
            (imageSize) => {
                if (imageSize === null) {
                    imagePlane.visible = false;
                } else {
                    const viewportSize = context.state.rendering.viewportSize;
                    if (viewportSize === null) {
                        _log.error('Viewport size is null when updating overview scene');
                        imagePlane.visible = false;
                        return;
                    }
                    const { target, zoom } = fullViewCameraParams(imageSize, viewportSize);

                    imagePlane.width = imageSize.width;
                    imagePlane.height = imageSize.height;
                    imagePlane.transform.setPosition(
                        imagePlane.width / 2,
                        imagePlane.height / 2,
                        0,
                    );
                    imagePlane.visible = true;

                    camera.zoom = zoom;
                    camera.target.copy(target);
                    camera.position.copy(target).add(vector3(0, 0, XRAY_CAMERA_TARGET_DISTANCE));
                }
            },
        ),
    );
}

function _updateMainScene(context: XRaySceneContext): StopHandle {
    const store = useXRaysStore();
    const imagePlane = context.state.nodes.imagePlane;

    /** Updates that happen based on the selected view-state */
    function updateFromViewState(viewState: XRayViewState): StopHandle {
        _log.debug(
            `Main view changed to ${viewState.viewId}: changing target to ` +
                `${formatVector(viewState.cameraTarget)} and zoom to ${viewState.cameraZoom}`,
        );

        imagePlane.material = viewState.imageMaterial;
        const camera = context.state.rendering.camera;

        return stopAll(
            watchImmediate(
                () => viewState.imageSize,
                (imageSize) => {
                    if (imageSize === null) {
                        _log.debug('Image size is null, hiding main image-plane');
                        imagePlane.visible = false;
                    } else {
                        imagePlane.width = imageSize.width;
                        imagePlane.height = imageSize.height;
                        imagePlane.transform.setPosition(
                            imagePlane.width / 2,
                            imagePlane.height / 2,
                            0,
                        );
                        imagePlane.visible = true;

                        camera.zoom = viewState.cameraZoom;
                        camera.target.copy(viewState.cameraTarget);
                        camera.position = viewState.cameraTarget
                            .clone()
                            .add(vector3(0, 0, XRAY_CAMERA_TARGET_DISTANCE));
                    }
                },
            ),
            watchImmediate(
                () => camera.target,
                (target) => {
                    _log.debug(`Target for ${viewState.viewId} set to ${formatVector(target)}`);
                    viewState.cameraTarget.copy(target);
                },
                { deep: true },
            ),
            watchImmediate(
                () => viewState.cameraTarget,
                (target) => {
                    camera.target.copy(target);
                    camera.position = target
                        .clone()
                        .add(vector3(0, 0, XRAY_CAMERA_TARGET_DISTANCE));
                },
                { deep: true },
            ),
            watchImmediate(
                () => camera.zoom,
                (zoom) => {
                    _log.debug(`Zoom for ${viewState.viewId} set to ${zoom}`);
                    viewState.cameraZoom = zoom;
                },
            ),
            watchImmediate(
                () => viewState.cameraZoom,
                (zoom) => (camera.zoom = zoom),
            ),
        );
    }

    let update: StopHandle | null = null;

    return stopAll(
        watchImmediate(
            () => store.mainViewId,
            (viewId) => {
                stop(update);
                update = updateFromViewState(store.viewState(viewId));
            },
        ),
        () => stop(update),
    );
}
